// The home page's Recent jobs list (#422). Pure: which jobs it shows and in
// what order; the screen reads CRM's list and the device, and draws the rows.
import type { RecentJob } from './types';

/** How many jobs the list shows (DD, 2026-10-22). */
const RECENT_JOBS_SHOWN = 10;

/** Compared as times, not text: CRM writes 03:00:00Z where a device writes 03:00:00.000Z. */
const later = (a: string, b: string) => Date.parse(a) > Date.parse(b);

/** A job on this device: when it was last opened or copied here, and how many of its changes are still to send. */
export interface DeviceJobState {
  job: { id: string; number: string; details?: { jobName: string | null }; openedAt: string; lastOpenedAt?: string };
  waiting: number;
}

/**
 * When the job was last opened here. A job copied or refreshed from CRM was
 * being opened (openedAt), which also covers jobs opened before opens were noted.
 */
const openedHere = ({ openedAt, lastOpenedAt }: DeviceJobState['job']): string => (lastOpenedAt && later(lastOpenedAt, openedAt) ? lastOpenedAt : openedAt);

export interface RecentJobRow extends RecentJob {
  onDevice: boolean;
  waiting: number;
}

const newestFirst = (a: RecentJob, b: RecentJob) => Date.parse(b.openedAt) - Date.parse(a.openedAt);

/**
 * CRM's list (opens from every device, as last fetched) with this device's own
 * opens, which may not have reached CRM yet: newest first, `shown` of them —
 * plus any further job here with changes still waiting, so nothing waiting to
 * sync is ever off the list.
 */
export function recentJobs(fromCrm: RecentJob[], onDevice: DeviceJobState[], shown = RECENT_JOBS_SHOWN): RecentJobRow[] {
  const opened = new Map(fromCrm.map((job) => [job.id.toLowerCase(), job]));
  for (const { job } of onDevice) {
    const known = opened.get(job.id.toLowerCase());
    const here = openedHere(job);
    if (!known || later(here, known.openedAt)) {
      opened.set(job.id.toLowerCase(), { id: job.id, number: job.number, name: known?.name ?? job.details?.jobName ?? null, openedAt: here });
    }
  }
  const device = new Map(onDevice.map((entry) => [entry.job.id.toLowerCase(), entry]));
  const row = (job: RecentJob): RecentJobRow => ({ ...job, onDevice: device.has(job.id.toLowerCase()), waiting: device.get(job.id.toLowerCase())?.waiting ?? 0 });

  const listed = [...opened.values()].sort(newestFirst).slice(0, shown).map(row);
  const shownIds = new Set(listed.map((job) => job.id.toLowerCase()));
  const stillWaiting = onDevice
    .filter(({ job, waiting }) => waiting > 0 && !shownIds.has(job.id.toLowerCase()))
    .flatMap(({ job }) => {
      const known = opened.get(job.id.toLowerCase());
      return known ? [row(known)] : [];
    });
  return [...listed, ...stillWaiting];
}
